import { Country, Currency } from "@lookiero/sty-psp-locale";
import { ItemToReturn, Order } from "../../../domain/entities/order/Order";
import { OrderSummaryForCheckout } from "../../../domain/projections/OrderSummaryForCheckout";
import { ReturningOrderPaymentBreakdown } from "../../../domain/projections/ReturningOrderPaymentBreakdown";
import { OrderRepository } from "../../../domain/repositories/OrderRepository";
import { Device, UpdateOrderItemsPayloadWithAuth, UUID } from "../../ui/shared/types";
import { createHttpClients } from "../shared/createHttpClients";
import {
  BreakdownDTO,
  OrderDTO,
  OrderDetailsDTO,
  mapOrderDTOtoOrder,
  mapOrderDetailDTOtoOrder,
  mapOrdersDTOtoOrders,
  mapReturningOrderPaymentBreakdownDTO,
  mapOrderDtoAndBreakDtoToOrderSummaryForCheckout,
  mapOrdersDTOtoCompletedOrders,
} from "./responseMapper";

interface StockOutResponseError {
  readonly itemIds: string[];
}

const isStockOutResponseError = (error: StockOutResponseError | unknown): error is StockOutResponseError => {
  return typeof error === "object" && error !== null && "itemIds" in error;
};

interface ReturnOrderItemsPayload {
  readonly orderId: string;
  readonly serviceId: string;
  readonly items: ItemToReturn[];
}

interface ReturnOrderItemsPayloadWithAuth extends ReturnOrderItemsPayload {
  readonly auth: string;
  readonly apiUrl: () => string;
}

class OrderRepositoryImpl implements OrderRepository {
  private static instance: OrderRepositoryImpl;

  private constructor() {}

  public static getInstance(): OrderRepositoryImpl {
    if (!OrderRepositoryImpl.instance) {
      OrderRepositoryImpl.instance = new OrderRepositoryImpl();
    }

    return OrderRepositoryImpl.instance;
  }

  public async addItem(payload: UpdateOrderItemsPayloadWithAuth): Promise<void> {
    try {
      const { apiUrl, auth, itemId, orderId, variantId, serviceId } = payload;
      const { httpPost } = createHttpClients(auth, apiUrl);
      await httpPost({
        endpoint: `/direct-buy/v1/add-item`,
        body: { itemId, orderId, serviceId, variantId },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getOrder(auth: string, apiUrl: () => string): Promise<Order> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const orderDTO = await httpGet<OrderDTO>({
        endpoint: `/direct-buy/v1/order`,
      });

      return mapOrderDTOtoOrder(orderDTO);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getOrderList(auth: string, apiUrl: () => string): Promise<Order[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const orderList = await httpGet<{ orders: OrderDTO[] }>({
        endpoint: `/direct-buy/v1/view-orders-for-logged-user`,
      });

      return mapOrdersDTOtoOrders(orderList.orders);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getCompletedOrderList(auth: string, apiUrl: () => string): Promise<Order[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const orderList = await httpGet<{ orders: OrderDTO[] }>({
        endpoint: `/direct-buy/v1/view-orders-for-logged-user`,
      });

      return mapOrdersDTOtoCompletedOrders(orderList.orders);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async placeOrder(orderId: UUID, auth: string, apiUrl: () => string): Promise<void> {
    try {
      const { httpPost } = createHttpClients(auth, apiUrl);
      await httpPost({
        endpoint: `/direct-buy/v1/place-order`,
        body: { orderId },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async removeItem(payload: UpdateOrderItemsPayloadWithAuth): Promise<void> {
    try {
      const { apiUrl, auth, orderId, serviceId, itemId } = payload;

      const { httpDelete } = createHttpClients(auth, apiUrl);
      await httpDelete({
        endpoint: `/direct-buy/v1/remove-item`,
        body: { itemId, orderId, serviceId },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async removeItems(auth: string, itemIds: string[], apiUrl: () => string): Promise<void> {
    try {
      const { httpDelete } = createHttpClients(auth, apiUrl);
      await httpDelete({
        endpoint: `/direct-buy/v1/remove-items`,
        body: { itemIds },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async createCheckout(auth: string, orderId: string, apiUrl: () => string): Promise<string> {
    try {
      const { httpPost } = createHttpClients(auth, apiUrl);
      return await httpPost<{ checkoutId: string }, string | Promise<string>>({
        endpoint: `/direct-buy/v1/checkout/${orderId}`,
        body: {},
        result: {
          success: ({ checkoutId }) => checkoutId,
          error: async (errorResponse) => {
            const errorBody = await errorResponse.json().catch(() => undefined);

            throw isStockOutResponseError(errorBody) ? errorBody : new Error(errorResponse.statusText);
          },
        },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getOrderDetails(auth: string, orderId: string, apiUrl: () => string): Promise<Order> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const orderDTO = await httpGet<OrderDetailsDTO>({
        endpoint: `/direct-buy/v1/view-order-details-by-id/${orderId}`,
      });

      return mapOrderDetailDTOtoOrder(orderDTO);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async requestReturnOrderItems(payload: ReturnOrderItemsPayloadWithAuth): Promise<void> {
    try {
      const { apiUrl, auth, orderId, serviceId, items } = payload;

      const { httpPut } = createHttpClients(auth, apiUrl);
      await httpPut({
        endpoint: `/direct-buy/v1/request-return`,
        body: { orderId, serviceId, items },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async applyPromo(
    auth: string,
    promoCode: string,
    device: Device,
    site: Country,
    apiUrl: () => string,
  ): Promise<void> {
    try {
      const { httpPost } = createHttpClients(auth, apiUrl);
      await httpPost({
        endpoint: `/direct-buy/v1/promotions/apply-promo`,
        body: { code: promoCode, device, site },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async discardPromo(auth: string, apiUrl: () => string): Promise<void> {
    try {
      const { httpDelete } = createHttpClients(auth, apiUrl);
      await httpDelete({
        endpoint: `/direct-buy/v1/promotions/discard-promo`,
        body: {},
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async moveStockOutsToWishlist(auth: string, apiUrl: () => string): Promise<void> {
    try {
      const { httpPost } = createHttpClients(auth, apiUrl);
      await httpPost({
        endpoint: `/direct-buy/v1/clean-stockouts`,
        body: {},
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getReturningOrderPaymentBreakdown(
    auth: string,
    checkoutId: string,
    totalPrice: number,
    currency: Currency,
    apiUrl: () => string,
  ): Promise<ReturningOrderPaymentBreakdown> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const breakdown = await httpGet<Parameters<typeof mapReturningOrderPaymentBreakdownDTO>[0]>({
        endpoint: `/web/api/checkout/v1/breakdown/${checkoutId}/${totalPrice}`,
      });

      return mapReturningOrderPaymentBreakdownDTO(breakdown, currency);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getSummaryForCheckout(auth: string, apiUrl: () => string): Promise<OrderSummaryForCheckout> {
    const { httpGet } = createHttpClients(auth, apiUrl);
    const orderDto = await httpGet<OrderDTO>({
      endpoint: `/direct-buy/v1/order`,
    });

    let breakdownDto: BreakdownDTO = {
      total: orderDto.orderPrice.amount,
      breakdown: {
        balance: 0,
        paymentInstrument: orderDto.orderPrice.amount,
      },
    };

    try {
      breakdownDto = await httpGet<BreakdownDTO>({
        endpoint: `/web/api/payments/v1/breakdown/${orderDto.orderPrice.amount}`,
      });

      return mapOrderDtoAndBreakDtoToOrderSummaryForCheckout(orderDto, breakdownDto);
    } catch (error) {
      console.error(error);
      return mapOrderDtoAndBreakDtoToOrderSummaryForCheckout(orderDto, breakdownDto);
    }
  }
}

export type { ReturnOrderItemsPayloadWithAuth, StockOutResponseError };
export { OrderRepositoryImpl, isStockOutResponseError };
